import { useUser } from "@shared/utils/hooks/misc";
import { sharedLib } from "@shared/services/shared.service";
import { useEffect, useState } from "react";
import { OpenMessage, PlatformType } from "@shared/types";
import { partition } from "lodash";
import { useDispatch } from "react-redux";

interface useLeaveReviewModalProps {
  openMessage: OpenMessage;
  onCancel?: () => void;
  redirectToTrustPilot: () => void;
  requestLeaveReview: () => Promise<void>;
  isMobile?: boolean;
}

export const useLeaveReviewModal = ({
  openMessage,
  onCancel,
  redirectToTrustPilot,
  requestLeaveReview,
  isMobile,
}: useLeaveReviewModalProps) => {
  const [rating, setRating] = useState<number>();
  const [review, setReview] = useState("");
  const [step, setStep] = useState(ReviewStep.RATING);
  const { user } = useUser(true);
  const [storeNpsResult] =
    sharedLib.sharedApi.common.useStoreNpsResultMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const dispatch = useDispatch();
  const userNpsKey = isMobile ? "should_display_nps" : "should_display_web_nps";

  useEffect(() => {
    if (user?.[userNpsKey] && !opened) {
      setIsOpen(true);
      setOpened(true);
    }
  }, [user]);

  const isReviewOverLength =
    !!review?.length && review.length > MAX_REVIEW_LENGTH;
  const isOkButtonDisabled =
    (step === ReviewStep.RATING && rating === undefined) ||
    (step === ReviewStep.REVIEW && isReviewOverLength);
  const isGoodRating = rating !== undefined && rating >= HIGH_RATING_THRESHOLD;

  const buttons = partition(
    Array(11)
      .fill(0)
      .map((_, index) => index),
    (rating) => rating < 6
  );

  const saveData = async (isSubmitClose?: boolean) => {
    const includeRating = isSubmitClose || step === ReviewStep.REVIEW;
    const includeText = isSubmitClose && step === ReviewStep.REVIEW;
    dispatch(
      sharedLib.slices.authentication.actions.updateUser({
        key: userNpsKey,
        value: false,
      })
    );
    storeNpsResult({
      rating: includeRating ? rating : undefined,
      text: includeText ? review : undefined,
      platform_type: isMobile ? PlatformType.MOBILE : PlatformType.WEBSITE,
    });
  };

  const closeModalAndSaveData = async (isSubmitClose?: boolean) => {
    setIsOpen(false);
    onCancel?.();
    try {
      await saveData(isSubmitClose);
    } catch {}
    setReview("");
    setRating(undefined);
    setStep(ReviewStep.RATING);
    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve("");
      }, 50);
    });
  };

  const onContinueClick = async () => {
    if (step === ReviewStep.RATING) {
      if (rating === undefined) return;
      if (rating < HIGH_RATING_THRESHOLD || !isMobile) {
        setStep(ReviewStep.REVIEW);
      } else {
        requestLeaveReview()
          .then(() => {
            closeModalAndSaveData(true).catch(() => {});
          })
          .catch(() => {
            setStep(ReviewStep.REVIEW);
          });
      }
    } else if (step === ReviewStep.REVIEW) {
      if (isGoodRating && !isMobile) {
        redirectToTrustPilot();
        await closeModalAndSaveData(true).catch(() => {});
      } else {
        await closeModalAndSaveData(true).catch(() => {});
        setTimeout(
          () => openMessage("Thank you for your feedback!", "success"),
          500
        );
      }
    }
  };

  const getOkText = () => {
    if (step === ReviewStep.RATING) return "Continue";
    else if (isGoodRating) return "Leave a Review";
    return "Submit";
  };

  const getCancelText = () => {
    if (step === ReviewStep.REVIEW && isGoodRating) return "Maybe Later";
    return "Close";
  };

  const footerData = {
    cancelText: getCancelText(),
    onCancel: closeModalAndSaveData,
    okText: getOkText(),
    onOk: onContinueClick,
    okDisabled: isOkButtonDisabled,
  };

  return {
    buttons,
    isOpen,
    rating,
    setRating,
    step,
    setStep,
    review,
    setReview,
    isReviewOverLength,
    footerData,
    isGoodRating,
  };
};

export enum ReviewStep {
  RATING,
  REVIEW,
}

export const MAX_REVIEW_LENGTH = 255;
export const HIGH_RATING_THRESHOLD = 9;
